import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import HybridWhatsAppManager from '../components/whatsapp/HybridWhatsAppManager';
import WhatsAppTemplateEditor from '../components/whatsapp/WhatsAppTemplateEditor';
import WhatsAppCountrySettings from '../components/whatsapp/WhatsAppCountrySettings';
import WhatsAppStats from '../components/whatsapp/WhatsAppStats';
import WhatsAppScheduler from '../components/whatsapp/WhatsAppScheduler';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`whatsapp-tabpanel-${index}`}
      aria-labelledby={`whatsapp-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const WhatsAppBaileysPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  const features = [
    {
      icon: <SecurityIcon color="primary" />,
      title: 'اتصال مباشر',
      description: 'اتصال مباشر مع WhatsApp بدون API خارجي'
    },
    {
      icon: <SpeedIcon color="success" />,
      title: 'سرعة عالية',
      description: 'إرسال فوري للرسائل بدون تأخير'
    },
    {
      icon: <GroupIcon color="info" />,
      title: 'رسائل جماعية',
      description: 'إمكانية إرسال رسائل لعدة عملاء في نفس الوقت'
    },
    {
      icon: <MessageIcon color="warning" />,
      title: 'رسائل غنية',
      description: 'دعم النصوص والصور والملفات'
    }
  ];



  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" mb={2}>
          <WhatsAppIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            خدمة WhatsApp الذكية
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          نظام WhatsApp متطور للتواصل المباشر مع العملاء
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="WhatsApp management tabs"
        >
          <Tab label="إدارة WhatsApp" id="whatsapp-tab-0" aria-controls="whatsapp-tabpanel-0" />
          <Tab label="تحرير قوالب الرسائل" id="whatsapp-tab-1" aria-controls="whatsapp-tabpanel-1" />
          <Tab label="إعدادات الدولة" id="whatsapp-tab-2" aria-controls="whatsapp-tabpanel-2" />
          <Tab label="إحصائيات الرسائل" id="whatsapp-tab-3" aria-controls="whatsapp-tabpanel-3" />
          <Tab label="الرسائل المجدولة" id="whatsapp-tab-4" aria-controls="whatsapp-tabpanel-4" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={4}>
          {/* Main Manager */}
          <Grid item xs={12} lg={8}>
            <HybridWhatsAppManager />
          </Grid>

        {/* Sidebar Info */}
        <Grid item xs={12} lg={4}>
          {/* Features */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ✨ المميزات
              </Typography>
              <List dense>
                {features.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {feature.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.title}
                      secondary={feature.description}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                ✅ جاهز للاستخدام
              </Typography>
              <Typography variant="body2" color="text.secondary">
                خدمة WhatsApp الذكية جاهزة للاستخدام مباشرة بدون الحاجة لتثبيت مكتبات إضافية.
                تستخدم روابط wa.me للتكامل المباشر مع تطبيق WhatsApp.
              </Typography>
            </CardContent>
          </Card>

          {/* Status Info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 معلومات الحالة
              </Typography>
              
              <Box mb={2}>
                <Box display="flex" alignItems="center" mb={1}>
                  <CheckIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="success.main">
                    الخدمة متاحة وجاهزة للاستخدام
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  💡 <strong>نصيحة:</strong> احتفظ بجلسة WhatsApp مفتوحة لضمان استمرار الاتصال.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Integration Info */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🔗 التكامل مع النظام
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" p={2}>
                <MessageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  رسائل الصيانة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  إرسال تلقائي لرسائل حالة الصيانة للعملاء
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box textAlign="center" p={2}>
                <GroupIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  إشعارات العملاء
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  تنبيهات فورية عند اكتمال الصيانة أو التحديثات
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box textAlign="center" p={2}>
                <SecurityIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  أمان عالي
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  اتصال آمن ومشفر مع WhatsApp مباشرة
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </TabPanel>

      {/* Template Editor Tab */}
      <TabPanel value={activeTab} index={1}>
        <WhatsAppTemplateEditor />
      </TabPanel>

      {/* Country Settings Tab */}
      <TabPanel value={activeTab} index={2}>
        <WhatsAppCountrySettings />
      </TabPanel>

      {/* Stats Tab */}
      <TabPanel value={activeTab} index={3}>
        <WhatsAppStats />
      </TabPanel>

      {/* Scheduler Tab */}
      <TabPanel value={activeTab} index={4}>
        <WhatsAppScheduler />
      </TabPanel>
    </Container>
  );
};

export default WhatsAppBaileysPage;
